/******/ (() => { // webpackBootstrap
/* eslint-disable no-undef */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    /* ===== Cache the DOM elements and set the initial state of the component ===== */
    this.sectionId = this.getAttribute('data-section-id');
    this.activeMediaClass = this.getAttribute('data-active-media-class');
    this.mediaPlayButton = this.querySelector('[data-media-play-button]');
    this.mediaId = this.getAttribute('data-media-id');
    this.autoPlayEnabled = this.getAttribute('data-auto-play') === 'true';
    this.mediaLayout = this.getAttribute('data-media-layout');
    this.controlsEnabled = this.getAttribute('data-show-controls') === 'true';
    this.mediaCount = this.getAttribute('data-media-count');
    this.isProductMedia = this.getAttribute('data-is-product-media') === 'true';
    this.clickToPlay = this.getAttribute('data-click-to-play') === 'true';
    this.useJsApi = this.getAttribute('data-use-js-api') === 'true';
    this.isBackgroundMedia = this.getAttribute('data-is-background-media') === 'true';
    this.sliderReady = !this.isProductMedia;
    this.visibilityObserver = null;
    this.isNearViewport = false;

    this.bindEventHandlers();
  }

  connectedCallback() {
    /* ===== Verify the initial setup and register the event listeners ===== */
    this.verifyInitialSetup();
    this.registerEventListeners();
    this.setupVisibilityObserver();
    this.syncAutoplayState();
  }

  disconnectedCallback() {
    this.disconnectVisibilityObserver();
    this.unregisterEventListeners();
  }

  bindEventHandlers() {
    /* ===== Bind the event handlers in order to maintain the correct context of 'this' ===== */
    this.handleMediaClick = this.handleMediaClick.bind(this);
    this.handleMediaLoad = this.handleMediaLoad.bind(this);
    this.pauseAllMedia = this.pauseAllMedia.bind(this);
    this.handlePauseMedia = this.handlePauseMedia.bind(this);
    this.handleAutoPlay = this.handleAutoPlay.bind(this);
    this.playMedia = this.playMedia.bind(this);
    this.handleSliderChange = this.handleSliderChange.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  verifyInitialSetup() {
    /* ===== Verify the initial setup of the component ===== */
    if (!this.mediaPlayButton && !this.isBackgroundMedia) {
      // Log an error if the play button is not found
      console.error('DeferredMedia: Play button not found!');
    }
    if (!this.mediaId) {
      // Log an error if the media ID is not found
      console.error('DeferredMedia: Media ID not found!');
    }

  }

  registerEventListeners() {
    /* ===== Attach the event listeners to the DOM elements ===== */
    eventBus.on('load:media', this.handleMediaLoad);
    eventBus.on('pause:media', this.handlePauseMedia);
    eventBus.on('play:media', this.playMedia);
    eventBus.on('slider:media:ready', this.handleAutoPlay);
    if (this.autoPlayEnabled) {
      document.addEventListener('slider:slide:change', this.handleSliderChange);
      window.addEventListener('resize', this.handleViewportChange);
    }
    this.mediaPlayButton?.addEventListener('click', this.handleMediaClick);
    if (this.clickToPlay) {
      this.addEventListener('click', this.handleMediaClick);
    }
  }

  unregisterEventListeners() {
    this.mediaPlayButton?.removeEventListener('click', this.handleMediaClick);
    if (this.clickToPlay) {
      this.removeEventListener('click', this.handleMediaClick);
    }
    eventBus.off('load:media', this.handleMediaLoad);
    eventBus.off('pause:media', this.handlePauseMedia);
    eventBus.off('play:media', this.playMedia);
    eventBus.off('slider:media:ready', this.handleAutoPlay);
    if (this.autoPlayEnabled) {
      document.removeEventListener('slider:slide:change', this.handleSliderChange);
      window.removeEventListener('resize', this.handleViewportChange);
    }
  }

  handleMediaLoad(event) {
    /* ===== Handle the load:media event and load the media if the media ID matches ===== */
    if (event.mediaId === this.mediaId && event.sectionId === this.sectionId) {
      this.loadMedia();
    }
  }

  handleMediaClick() {
    /* ===== Handle the click event on the media play button ===== */
    this.loadMedia();
  }

  hideLoadingIcon() {
    this.loadingIcon?.classList.add('hidden');
  }

  setupVisibilityObserver() {
    if (!this.autoPlayEnabled || typeof IntersectionObserver === 'undefined') return;

    this.visibilityObserver = new IntersectionObserver(this.handleVisibilityChange, {
      rootMargin: '150px 0px',
      threshold: 0.01
    });

    this.visibilityObserver.observe(this);
  }

  disconnectVisibilityObserver() {
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
  }

  handleVisibilityChange(entries) {
    const entry = entries[0];
    if (!entry) return;

    this.isNearViewport = entry.isIntersecting;
    this.syncAutoplayState();
  }

  handleSliderChange() {
    if (!this.autoPlayEnabled || !this.getSlideElement()) return;
    this.syncAutoplayState();
  }

  handleViewportChange() {
    if (!this.autoPlayEnabled) return;
    this.syncAutoplayState();
  }

  syncAutoplayState() {
    if (!this.autoPlayEnabled) return;

    if (!this.shouldAutoPlayNow()) {
      this.pauseLoadedMedia();
      return;
    }

    if (!this.getAttribute('data-media-loaded')) {
      this.loadMedia();
      return;
    }

    const mediaEl = this.getLoadedMediaElement();
    if (!mediaEl || this.isMediaCurrentlyPlaying(mediaEl)) return;

    this.playMedia({ sectionId: this.sectionId, mediaEl });
  }

  shouldAutoPlayNow() {
    if (this.isProductMedia && !this.sliderReady) return false;
    return this.isComponentVisible() && this.isNearViewportOrVisible() && this.isActiveSlide();
  }

  isComponentVisible() {
    if (!this.isConnected) return false;

    const styles = getComputedStyle(this);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;

    const rect = this.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  isNearViewportOrVisible() {
    if (this.isNearViewport) return true;
    if (typeof window === 'undefined') return true;

    const preloadOffset = 150;
    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return rect.bottom >= -preloadOffset
      && rect.right >= -preloadOffset
      && rect.top <= viewportHeight + preloadOffset
      && rect.left <= viewportWidth + preloadOffset;
  }

  getSlideElement() {
    let currentElement = this.parentElement;

    while (currentElement && currentElement !== document.body) {
      const isSlide = Array.from(currentElement.classList || []).some(className => className.startsWith('swiper-slide'));
      if (isSlide) return currentElement;
      currentElement = currentElement.parentElement;
    }

    return null;
  }

  isActiveSlide() {
    const slideElement = this.getSlideElement();
    if (!slideElement) return true;
    if (slideElement.classList.contains('swiper-slide-active')) return true;

    const slideWrapper = slideElement.parentElement;
    if (!slideWrapper) return true;

    const activeSlide = slideWrapper.querySelector('.swiper-slide-active');
    if (activeSlide) return activeSlide === slideElement;

    const slides = Array.from(slideWrapper.children).filter(child => Array.from(child.classList || []).some(className => className.startsWith('swiper-slide')) && !child.classList.contains('swiper-slide-duplicate'));

    return slides.length ? slides[0] === slideElement : true;
  }

  getLoadedMediaElement() {
    return this.querySelector('video, iframe, model-viewer');
  }

  isMediaCurrentlyPlaying(mediaEl) {
    if (!mediaEl) return false;
    if (mediaEl.tagName === 'VIDEO') return !mediaEl.paused && !mediaEl.ended;
    if (mediaEl.tagName === 'IFRAME') return mediaEl.dataset.mediaIsPlaying === 'true';
    return false;
  }

  pauseLoadedMedia() {
    const mediaEl = this.getLoadedMediaElement();
    if (!mediaEl) return;

    this.pauseMedia(mediaEl);
  }

  handleAutoPlay(event) {
    if (!event.sectionId || event.sectionId !== this.sectionId) return;
    this.sliderReady = true;
    this.syncAutoplayState();
  }

  handlePauseMedia() {
    /* ===== Handle the 'pause:media' event ===== */
    this.pauseAllMedia().then(() => {
      this.removeAutoplayFromLoadedMedia();
    }).catch(error => {
      console.error('Error handling pause media:', error);
    });
  }

  playMedia(event) {
    const { sectionId, mediaEl } = event;
    if (this.sectionId !== sectionId || !mediaEl || !this.autoPlayEnabled || !this.getAttribute('data-media-loaded') || this.isMediaCurrentlyPlaying(mediaEl)) return;

    this.pauseAllMedia().then(() => {
      // Get the media type
      const mediaType = mediaEl.tagName;
      // Play the media based on the type
      if (mediaType === 'VIDEO') {
        mediaEl.play();
      } else if (mediaType === 'IFRAME') {
        try {
          mediaEl.contentWindow.postMessage(this.messageFn('play', mediaEl.src), '*');
          // Set the flag to indicate this media is playing
          mediaEl.dataset.mediaIsPlaying = 'true';
        } catch (error) {
          console.error('Error playing iframe media:', error);
        }
      }
    });
  }

  loadMedia() {
    /* ===== Load the media element and post-process it ===== */
    if (!this.mediaId || this.getAttribute('data-media-loaded')) return;
    
    this.pauseAllMedia().then(() => {
      const template = this.querySelector('template');
      const mediaElement = template?.content?.querySelector('[data-media-wrapper]');
      if (!mediaElement) return;

      // check if the media is active when the layout is set to 'slider'
      if (this.isProductMedia && this.mediaLayout != 'grid' && this.mediaCount > 1 && !this.isActiveSlide()) return;
  
      const media = mediaElement.cloneNode(true);
      this.appendChild(media);
      this.setAttribute('data-media-loaded', true);
      media.classList.add(this.activeMediaClass);
      if (this.mediaPlayButton) this.mediaPlayButton.style.display = 'none';
      template.remove();

      const mediaEl = media.querySelector('video, iframe, model-viewer');

      // Show the loading icon
      if (mediaEl && mediaEl.tagName === 'VIDEO') {
        this.loadingIcon = this.querySelector('[data-media-loading-icon]');
        if (this.loadingIcon) this.loadingIcon.classList.remove('hidden');
      }
  
      this.postProcessMedia(mediaEl);
    });
  }

  loadJsApi() {
    let videoType;
    /* ===== Load the js api ===== */
    const script = document.createElement('script');
    // Determine the video provider
    if (this.querySelector('iframe')?.src?.includes('vimeo')) {
      // Set the video type
      videoType = 'vimeo';
      // Set the video api source
      script.src = 'https://player.vimeo.com/api/player.js';
      // Set the script onload event
      script.onload = () => {
        eventBus.emit('video:api:loaded');
      };
    } else if (this.querySelector('iframe')?.src?.includes('youtube')) {
      // Set the video type
      videoType = 'youtube';
      // Set the video api source
      script.src = 'https://www.youtube.com/iframe_api';
      // Set the script onload event
      script.onload = () => {
        eventBus.emit('video:api:loaded');
      };
    }

    // Add the script to the head
    document.head.appendChild(script);

    // Initialize the YouTube or Vimeo player
    this.initializeJsApi(videoType);
  }

  initializeJsApi(type) {
    // Initialize the YouTube or Vimeo player
    eventBus.on('video:api:loaded', () => {
      if (type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => {
          const iframe = this.querySelector('iframe');
          const player = new YT.Player(iframe, {
            events: {
              // Handle the state change event
              onStateChange: (state) => {
                // Check if the video has ended
                const ended = state.data === 0;
                const started = state.data === 1;

                if (started) {
                  // Emit the video started event
                  eventBus.emit('video:started', { details: { sectionId: this.sectionId, player: player, el: iframe } });
                }

                if (ended) {
                  // Emit the video ended event
                  eventBus.emit('video:ended', { details: { sectionId: this.sectionId, player: player, el: iframe } });
                }
              }
            }
          });
        };
      } else if (type === 'vimeo') {
        // Get the iframe element
        const iframe = this.querySelector('iframe');
        // Initialize the Vimeo player
        const player = new Vimeo.Player(iframe);

        // Handle the play event
        player.on('play', () => {
          // Emit the video started event
          eventBus.emit('video:started', { details: { sectionId: this.sectionId, player: player, el: iframe } });
        });

        // Handle the ended event
        player.on('ended', () => {
          // Emit the video ended event
          eventBus.emit('video:ended', { details: { sectionId: this.sectionId, player: player, el: iframe } });
        });
      }
    });
  }

  postProcessMedia(mediaEl) {
    /* ===== Post-process the media element and load specific features based on the media type ===== */
    if (!mediaEl) return;
  
    mediaEl.style.pointerEvents = 'auto';
    this.loadSpecificMediaFeatures(mediaEl);
  }

  loadSpecificMediaFeatures(mediaEl) {
    /* ===== Load specific features based on the media type ===== */
    switch (mediaEl.tagName) {
      case 'MODEL-VIEWER':
        this.loadModelFeatures(mediaEl);
        break;
      case 'VIDEO':
        mediaEl.play().catch(error => {
          console.error('Error playing video:', error);
        });
        this.hideLoadingIcon(); // Hide loading icon in all instances as sometimes eg on Safari the video plays but an error is thrown
        if (!this.controlsEnabled) this.setupVideoToggle(mediaEl);

        // Emit the video ended event when the video ends
        mediaEl.addEventListener('ended', () => {
          eventBus.emit('video:ended', { details: { sectionId: this.sectionId, player: mediaEl, el: mediaEl } });
        });

        // Emit the video started event when the video starts
        mediaEl.addEventListener('play', () => {
          eventBus.emit('video:started', { details: { sectionId: this.sectionId, player: mediaEl, el: mediaEl } });
        });
        break;
      case 'IFRAME': {
        // Set up the iframe toggle
        const mediaSrc = mediaEl.src;
        if (mediaSrc && mediaSrc.includes('vimeo')) mediaEl.classList.add('pointer-events-none');
        mediaEl.dataset.mediaIsPlaying = 'true';
        if (!this.controlsEnabled) this.setupIframeToggle(mediaEl);

        // Load the js api if the useJsApi attribute is true
        if (this.useJsApi) {
          this.loadJsApi();
        }
        break;
      }
    }
  }

  setupVideoToggle(videoEl) {
    if (!videoEl || videoEl.tagName !== 'VIDEO') return;
    if (this.isProductMedia) {
      if (!videoEl.closest('.swiper-slide-active')) return;
    }
    
    // Add click listener to toggle play/pause after initial play
    videoEl.addEventListener('click', () => {
      if (videoEl.paused) {
        videoEl.play();
      } else {
        videoEl.pause();
      }
    });
  }

  setupIframeToggle(iframeEl) {
    if (!iframeEl || iframeEl.tagName !== 'IFRAME') return;
    if (this.isProductMedia) {
      if (!iframeEl.closest('.swiper-slide-active')) return;
    }
    
    // Add click listener to toggle play/pause after initial play for YouTube or Vimeo iframe
    const iframeWrapper = iframeEl.closest('[data-media-wrapper]');
    if (!iframeWrapper) return;

    iframeWrapper.addEventListener('click', () => {
      const src = iframeEl.src;
      const isPlaying = iframeEl.dataset.mediaIsPlaying === 'true';
  
      if (this.isYouTubeOrVimeo(src)) {
        const message = isPlaying ? 'pause' : 'play';
  
        try {
          // Play or pause the video
          iframeEl.contentWindow.postMessage(this.messageFn(message, iframeEl.src), '*');
          // Toggle play/pause state
          iframeEl.dataset.mediaIsPlaying = isPlaying ? 'false' : 'true';
        } catch (error) {
          console.error('Error playing iframe media:', error);
        }
      }
    });
  }
  
  isYouTubeOrVimeo(src) {
    return src.includes('youtube') || src.includes('vimeo');
  }

  loadModelFeatures(modelViewerElement) {
    /* ===== Load the model-viewer features and initialize the Shopify XR ===== */
    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors) return;

          const modelViewerUI = new Shopify.ModelViewerUI(modelViewerElement);
          modelViewerElement.modelViewerUI = modelViewerUI;

          this.addModelInteractionListeners(modelViewerElement);
          this.initializeShopifyXR();
        },
      }
    ]);
  }

  addModelInteractionListeners(modelViewerElement) {
    /* ===== Add event listeners to disable/enable swiping on the main swiper ===== */
    const events = ['mousedown', 'mouseup', 'touchstart', 'touchend'];
    const actionMap = {
      'mousedown': 'disable-swiping',
      'mouseup': 'enable-swiping',
      'touchstart': 'disable-swiping',
      'touchend': 'enable-swiping'
    };

    events.forEach(event => {
      modelViewerElement.addEventListener(event, () => {
        eventBus.emit(actionMap[event], { sectionId: this.sectionId });
      });
    });
  }

  initializeShopifyXR() {
    /* ===== Initialize the Shopify XR and setup the XR elements ===== */
    Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: (errors) => {
          if (errors) return;
          document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR());
        },
      }
    ]);
  }

  setupShopifyXR() {
    /* ===== Setup the Shopify XR elements and add the models to the XR viewer ===== */
    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  }

  pauseAllMedia() {
    /* ===== Pause all media elements on the page ===== */
    return new Promise((resolve, reject) => {
      try {
        const allMedia = document.querySelectorAll('deferred-media:not([data-auto-play="true"][data-is-product-media="false"] iframe:not(.is-background-media)), deferred-media:not([data-auto-play="true"][data-is-product-media="false"] video:not(.is-background-media)), model-viewer');
        const pausePromises = Array.from(allMedia).map(media => this.pauseMedia(media));

        Promise.all(pausePromises).then(() => resolve());
      } catch (error) {
        reject(error);
      }
    });
  }

  removeAutoplayFromLoadedMedia() {
    /* ===== Remove autoplay from loaded media elements ===== */
    const loadedMedia = this.querySelectorAll('[data-media-loaded] iframe, [data-media-loaded] video');
    loadedMedia.forEach(media => {
      this.removeAutoplayFromMedia(media);
    });
  }

  pauseMedia(media) {
    if (media.tagName === 'IFRAME') {
      try {
        // Pause the iframe media
        media.contentWindow.postMessage(this.messageFn('pause', media.src), '*');
        // Set the flag to indicate this media is not playing
        media.dataset.mediaIsPlaying = 'false';
      } catch (error) {
        console.error('Error pausing iframe media:', error);
      }
    } else if (media.tagName === 'VIDEO' && !media.paused) {
      return media.pause();
    } else if (media.tagName === 'MODEL-VIEWER' && media.modelViewerUI) {
      media.modelViewerUI.pause();
    }
  }

  removeAutoplayFromMedia(media) {
    if (!media || this.autoPlayEnabled) return;
    if (media.tagName === 'IFRAME' && media.src.includes('autoplay=1')) media.src = media.src.replace('autoplay=1', 'autoplay=0');
    if (media.tagName === 'VIDEO' && media.hasAttribute('autoplay')) media.removeAttribute('autoplay');
  }

  messageFn(action, src) {
    /* ===== Create a message function to pause/play the media elements ===== */
    if (src.includes('youtube')) {
      return JSON.stringify({ event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: [] });
    } else if (src.includes('vimeo')) {
      return JSON.stringify({ method: action });
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/******/ })()
;
